import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Typography,
  Stack,
  Box,
  Chip,
  TableSortLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

/**
 * Columnas de la tabla de nuevos miembros.
 * id: key del objeto member, label: texto del header, sortable: permite ordenar
 */
const COLUMNS = [
  { id: "name", label: "Name", sortable: true },
  { id: "dob", label: "DOB", sortable: false },
  { id: "gender", label: "Gender", sortable: false },
  { id: "center", label: "Center", sortable: true },
  { id: "pcp", label: "PCP", sortable: true },
  { id: "effectiveDate", label: "Effective Date", sortable: true },
  { id: "plan", label: "Plan", sortable: false },
];

const DEFAULT_ROWS_PER_PAGE = 20;

/**
 * Dialog que muestra los nuevos miembros con búsqueda, paginación y ordenamiento.
 *
 * @param {{ open: boolean, onClose: () => void, loadData: () => Promise<{total: number, members: Array}> }} props
 */
export default function NewMembersDialog({ open, onClose, loadData }) {
  const [members, setMembers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  // ── Carga de datos al abrir ─────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    setSearch("");
    setPage(0);
    setOrderBy("name");
    setOrder("asc");

    loadData()
      .then((result) => {
        if (cancelled) return;
        setMembers(result?.members || []);
        setTotalCount(result?.total || result?.members?.length || 0);
      })
      .catch(() => {
        if (!cancelled) {
          setMembers([]);
          setTotalCount(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, loadData]);

  // ── Sorting ─────────────────────────────────────────────────────
  const handleSort = useCallback(
    (columnId) => {
      const isAsc = orderBy === columnId && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(columnId);
      setPage(0);
    },
    [orderBy, order]
  );

  // ── Filtrado + ordenamiento ─────────────────────────────────────
  const filteredAndSorted = useMemo(() => {
    let result = members;

    // Filtro por búsqueda
    if (search.trim()) {
      const term = search.trim().toLowerCase();
      result = result.filter(
        (m) =>
          (m.name && m.name.toLowerCase().includes(term)) ||
          (m.center && m.center.toLowerCase().includes(term))
      );
    }

    // Ordenamiento
    result = [...result].sort((a, b) => {
      const aVal = a[orderBy] ?? "";
      const bVal = b[orderBy] ?? "";
      const cmp =
        typeof aVal === "string"
          ? aVal.localeCompare(bVal, undefined, { sensitivity: "base" })
          : aVal - bVal;
      return order === "asc" ? cmp : -cmp;
    });

    return result;
  }, [members, search, orderBy, order]);

  // ── Paginación ──────────────────────────────────────────────────
  const paginatedRows = useMemo(
    () =>
      filteredAndSorted.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [filteredAndSorted, page, rowsPerPage]
  );

  const handleChangePage = (_event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" fontWeight={700}>
            New Members
          </Typography>
          {!loading && (
            <Chip
              label={`${filteredAndSorted.length} of ${totalCount}`}
              size="small"
              color="success"
              variant="outlined"
            />
          )}
        </Stack>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 300,
              gap: 2,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body2" color="text.secondary">
              Loading new members...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Search bar */}
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search by name or center..."
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: 400 }}
              />
            </Box>

            {/* Table */}
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {COLUMNS.map((col) => (
                      <TableCell
                        key={col.id}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          whiteSpace: "nowrap",
                        }}
                        sortDirection={
                          col.sortable && orderBy === col.id ? order : false
                        }
                      >
                        {col.sortable ? (
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLUMNS.length} align="center">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 4 }}
                        >
                          {search
                            ? "No members match your search."
                            : "No new members found."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((member, idx) => (
                      <TableRow
                        key={member.id || `${member.name}-${idx}`}
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {member.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{member.dob}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.gender}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              bgcolor:
                                member.gender === "F" ? "#FCE4EC" : "#E3F2FD",
                              color:
                                member.gender === "F" ? "#AD1457" : "#1565C0",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {member.center}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{member.pcp}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {member.effectiveDate}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{member.plan}</Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredAndSorted.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
